import { format } from 'date-fns';
import { Button } from 'primereact/button';
import { Card } from 'primereact/card';
import { Dialog } from 'primereact/dialog';
import { Tooltip } from 'primereact/tooltip';
import { useCallback, useEffect, useMemo, useState } from 'react';
import ReactFlow, {
  Background,
  Edge,
  Handle,
  MarkerType,
  MiniMap,
  Node,
  NodeProps,
  NodeTypes,
  Position,
  ReactFlowProvider,
  useEdgesState,
  useNodesState,
  useReactFlow,
} from 'reactflow';
import 'reactflow/dist/style.css';
import { TaskName, TaskState, Workflow } from '~/atoms/schemas/workflowSchemas';
import { TaskEvents } from '../workflow/TaskEvents';

interface WorkflowGraphProps {
  workflow: Workflow;
  className?: string;
}

const STATUS_COLORS = {
  completed: '#4caf50',
  running: '#2196f3',
  pending: '#ff9800',
  failed: '#f44336',
  default: '#999',
} as const;

const LAYOUT = {
  nodeSpacing: 200,
  initialX: 100,
} as const;

// Helper to determine the task status color
const getStatusColor = (taskState: TaskState) => {
  if (taskState.output) return STATUS_COLORS.completed;
  return Object.keys(taskState.events).length > 0
    ? STATUS_COLORS.running
    : STATUS_COLORS.pending;
};

// Status badge component
const StatusBadge = ({ status }: { status: string }) => {
  const getStatusStyle = () => {
    switch (status) {
      case 'completed':
        return { backgroundColor: '#4caf50' };
      case 'running':
        return { backgroundColor: '#2196f3' };
      case 'pending':
        return { backgroundColor: '#ff9800' };
      case 'failed':
        return { backgroundColor: '#f44336' };
      default:
        return { backgroundColor: '#999' };
    }
  };

  return (
    <div
      style={{
        borderRadius: '12px',
        padding: '2px 10px',
        fontSize: '12px',
        fontWeight: 'bold',
        color: 'var(--bq-button-text)',
        display: 'inline-block',
        ...getStatusStyle(),
      }}
    >
      {status}
    </div>
  );
};

// Badge to indicate checkpoint status
const CheckpointBadge = ({ should_continue }: { should_continue: boolean }) => {
  if (!should_continue) return null;

  const getColor = () => {
    if (should_continue === true) return '#4caf50';
    if (should_continue === false) return '#f44336';
    return '#ff9800'; // pending/waiting
  };

  return (
    <div
      style={{
        position: 'absolute',
        top: '-10px',
        right: '-10px',
        borderRadius: '50%',
        width: '24px',
        height: '24px',
        backgroundColor: getColor(),
        border: '2px solid #1e1e1e',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
      className="checkpoint-badge"
    >
      <i
        className="pi pi-flag"
        style={{ fontSize: '12px', color: 'var(--bq-button-text)' }}
      />
    </div>
  );
};

interface TaskNodeData {
  label: string;
  taskState: TaskState;
  taskName: string;
}

// Custom node component for tasks
const TaskNode = ({ data, isConnectable }: NodeProps<TaskNodeData>) => {
  const taskState = data.taskState as TaskState;
  const events = Object.values(taskState.events || {});
  const hasEvents = events.length > 0;
  const hasInputs = !!taskState.task_input;
  const hasOutputs = !!taskState.output;
  const hasCheckpoint = !!taskState.checkpoint;

  // Determine status based on task state
  let status = 'pending';
  if (taskState.output) status = 'completed';
  else if (events.length > 0) status = 'running';

  const createdAt = new Date(taskState.created_at);

  const [showDetails, setShowDetails] = useState(false);

  return (
    <>
      <div
        style={{
          padding: '10px',
          borderRadius: '5px',
          backgroundColor: 'var(--bq-surface-elevated)',
          border: `2px solid ${getStatusColor(taskState)}`,
          color: 'var(--bq-text-primary)',
          width: '250px',
          position: 'relative',
        }}
      >
        <Handle
          type="target"
          position={Position.Left}
          isConnectable={isConnectable}
          style={{ background: '#555' }}
        />

        {hasCheckpoint && (
          <CheckpointBadge
            should_continue={taskState.checkpoint?.should_continue ?? false}
          />
        )}

        <div
          style={{ fontWeight: 'bold', marginBottom: '5px', fontSize: '16px' }}
        >
          {data.label}
        </div>

        <div
          style={{
            marginBottom: '8px',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <StatusBadge status={status} />
          <span style={{ fontSize: '12px', color: 'var(--bq-text-secondary)' }}>
            {format(createdAt, 'MMM dd HH:mm:ss')}
          </span>
        </div>

        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            marginTop: '10px',
          }}
        >
          {hasInputs && (
            <Button
              icon="pi pi-arrow-down"
              size="small"
              text
              severity="secondary"
              tooltip="Has Input"
              tooltipOptions={{ position: 'bottom' }}
            />
          )}
          {hasOutputs && (
            <Button
              icon="pi pi-arrow-up"
              size="small"
              text
              severity="success"
              tooltip="Has Output"
              tooltipOptions={{ position: 'bottom' }}
            />
          )}
          {hasEvents && (
            <Button
              icon="pi pi-history"
              size="small"
              text
              severity="info"
              tooltip={`${events.length} Events`}
              tooltipOptions={{ position: 'bottom' }}
            />
          )}
          <Button
            icon="pi pi-info-circle"
            size="small"
            text
            onClick={() => setShowDetails(true)}
            tooltip="View Details"
            tooltipOptions={{ position: 'bottom' }}
          />
        </div>

        <Handle
          type="source"
          position={Position.Right}
          isConnectable={isConnectable}
          style={{ background: '#555' }}
        />
      </div>

      <Dialog
        header={`Task Details: ${data.label}`}
        visible={showDetails}
        onHide={() => setShowDetails(false)}
        style={{ width: '80vw', maxWidth: '900px' }}
        maximizable
      >
        <div className="task-details p-3">
          <div className="grid">
            <div className="col-12">
              <h3 className="mb-2">Basic Information</h3>
              <div className="p-2 border-round bg-gray-900">
                <div>
                  <strong>Task ID:</strong> {taskState.task_id}
                </div>
                <div>
                  <strong>Created:</strong>{' '}
                  {format(
                    new Date(taskState.created_at),
                    'MMM dd, yyyy HH:mm:ss',
                  )}
                </div>
                <div>
                  <strong>Status:</strong> <StatusBadge status={status} />
                </div>
                {hasCheckpoint && (
                  <div>
                    <strong>Checkpoint:</strong>{' '}
                    {taskState.checkpoint?.checkpoint_id}
                    <span className="ml-2">
                      <StatusBadge
                        status={
                          taskState.checkpoint?.should_continue
                            ? 'proceed'
                            : taskState.checkpoint?.should_continue === false
                              ? 'blocked'
                              : 'waiting'
                        }
                      />
                    </span>
                  </div>
                )}
              </div>
            </div>

            {hasInputs && (
              <div className="col-12 mt-3">
                <h3 className="mb-2">Input</h3>
                <div
                  className="p-2 border-round bg-gray-900 overflow-auto"
                  style={{ maxHeight: '200px' }}
                >
                  <pre style={{ margin: 0, color: 'var(--bq-text-primary)' }}>
                    {JSON.stringify(taskState.task_input, null, 2)}
                  </pre>
                </div>
              </div>
            )}

            {hasOutputs && (
              <div className="col-12 mt-3">
                <h3 className="mb-2">Output</h3>
                <div
                  className="p-2 border-round bg-gray-900 overflow-auto"
                  style={{ maxHeight: '200px' }}
                >
                  <pre style={{ margin: 0, color: 'var(--bq-text-primary)' }}>
                    {JSON.stringify(taskState.output, null, 2)}
                  </pre>
                </div>
              </div>
            )}

            {hasEvents && (
              <div className="col-12 mt-3">
                <h3 className="mb-2">Events</h3>
                <TaskEvents events={events} />
              </div>
            )}
          </div>
        </div>
      </Dialog>
    </>
  );
};

// Add this utility function near the top of the file
const formatTaskName = (str: string) => {
  return str
    .replace(/([A-Z])/g, ' $1')
    .trim()
    .replace(/^./, (match) => match.toUpperCase());
};

// Add at the top with other imports
type EdgeStyle = {
  stroke: string;
  strokeWidth: number;
  strokeDasharray?: string;
};

// Flow graph with ReactFlow provider
const FlowGraph = ({
  workflow,
  height = '65vh',
}: WorkflowGraphProps & { height?: string }) => {
  // Node types definition
  const nodeTypes: NodeTypes = useMemo(
    () => ({
      taskNode: TaskNode,
    }),
    [],
  );

  // Create nodes and edges from workflow data
  const { nodes: initialNodes, edges: initialEdges } = useMemo(() => {
    // Get the task states
    const taskStates = workflow.task_states;
    const taskKeys = Object.keys(taskStates);

    // Filter for valid task names
    const validTaskNames = taskKeys as TaskName[];

    // Sort tasks by creation time
    const orderedTasks = validTaskNames
      .map((name) => ({
        name,
        createdAt: new Date(
          taskStates[name]?.created_at || new Date().toISOString(),
        ).getTime(),
      }))
      .sort((a, b) => a.createdAt - b.createdAt);

    // Create nodes
    const nodes: Node[] = orderedTasks.map((task, index) => ({
      id: task.name,
      type: 'taskNode',
      data: {
        label: formatTaskName(task.name),
        taskState: taskStates[task.name],
        taskName: task.name,
      },
      position: { x: 100, y: index * LAYOUT.nodeSpacing },
    }));

    // Create edges with different styles
    const edges: Edge[] = orderedTasks.slice(0, -1).map((task, index) => {
      const nextTask = orderedTasks[index + 1];
      const sourceTaskState = taskStates[task.name];

      // Determine edge style based on checkpoint status
      let animated = !sourceTaskState?.output;
      let style: EdgeStyle = { stroke: '#888', strokeWidth: 2 };
      const markerEnd = { type: MarkerType.ArrowClosed };

      // If there's a checkpoint, customize the edge
      if (sourceTaskState?.checkpoint) {
        if (sourceTaskState.checkpoint.should_continue === true) {
          style = { stroke: '#4caf50', strokeWidth: 3 };
        } else if (sourceTaskState.checkpoint.should_continue === false) {
          style = {
            stroke: '#f44336',
            strokeWidth: 3,
            strokeDasharray: '5,5',
          };
        } else {
          style = {
            stroke: '#ff9800',
            strokeWidth: 3,
            strokeDasharray: '5,5',
          };
          animated = true;
        }
      }

      return {
        id: `e${task.name}-${nextTask.name}`,
        source: task.name,
        target: nextTask.name,
        animated,
        style,
        markerEnd,
        label: sourceTaskState?.checkpoint ? 'Checkpoint' : undefined,
        labelStyle: { fill: '#fff', fontWeight: 'bold' },
        labelBgStyle: { fill: '#333', fillOpacity: 0.7 },
      };
    });

    // Add workflow input node if data is available
    if (workflow.data) {
      const inputNodeId = 'workflow-input';
      nodes.unshift({
        id: inputNodeId,
        type: 'default',
        data: {
          label: (
            <div style={{ padding: '10px', color: 'var(--bq-text-primary)' }}>
              <div
                style={{
                  fontWeight: 'bold',
                  fontSize: '14px',
                  marginBottom: '5px',
                }}
              >
                Workflow Input
              </div>
              <div style={{ fontSize: '12px' }}>
                {workflow.data.type && <div>Type: {workflow.data.type}</div>}
                {workflow.data.from_address && (
                  <div>From: {workflow.data.from_address}</div>
                )}
                {workflow.data.subject && (
                  <div>Subject: {workflow.data.subject}</div>
                )}
              </div>
            </div>
          ),
        },
        position: { x: 100, y: -150 },
        style: {
          background: 'var(--bq-surface-elevated)',
          border: '2px solid #5caedc',
          borderRadius: '5px',
          color: 'var(--bq-text-primary)',
          width: '250px',
        },
      });

      // Add edge from input to first task
      if (orderedTasks.length > 0) {
        edges.unshift({
          id: `e${inputNodeId}-${orderedTasks[0].name}`,
          source: inputNodeId,
          target: orderedTasks[0].name,
          style: { stroke: '#5caedc', strokeWidth: 2 },
          animated: true,
          markerEnd: { type: MarkerType.ArrowClosed },
        });
      }
    }

    return { nodes, edges };
  }, [workflow]);

  // Set up React Flow state
  const [nodes, setNodes, onNodesChange] = useNodesState(initialNodes);
  const [edges, _setEdges, onEdgesChange] = useEdgesState(initialEdges);

  // Layout nodes in automatic arrangement
  const reactFlowInstance = useReactFlow();

  const onLayout = useCallback(() => {
    const layoutedNodes = nodes.map((node, index) => {
      // Keep the input node at the top
      if (node.id === 'workflow-input') {
        return {
          ...node,
          position: { x: LAYOUT.initialX, y: -150 },
        };
      }

      return {
        ...node,
        position: { x: LAYOUT.initialX, y: index * LAYOUT.nodeSpacing },
      };
    });
    setNodes(layoutedNodes);
  }, [nodes, setNodes]);

  const fitView = useCallback(() => {
    setTimeout(() => {
      reactFlowInstance.fitView({ padding: 0.2 });
    }, 50);
  }, [reactFlowInstance]);

  useEffect(() => {
    const { nodes: newNodes, edges: newEdges } = initializeNodesAndEdges();
    setNodes(newNodes);
    _setEdges(newEdges);

    // Automatically fit view when nodes change
    setTimeout(fitView, 100);
  }, [workflow]);

  const initializeNodesAndEdges = useCallback(() => {
    // Get the task states
    const taskStates = workflow.task_states;
    const taskKeys = Object.keys(taskStates);

    // Filter for valid task names
    const validTaskNames = taskKeys;

    // Sort tasks by creation time
    const orderedTasks = validTaskNames
      .map((name) => ({
        name,
        createdAt: new Date(
          taskStates[name]?.created_at || new Date().toISOString(),
        ).getTime(),
      }))
      .sort((a, b) => a.createdAt - b.createdAt);

    // Create nodes
    const nodes: Node[] = orderedTasks.map((task, index) => ({
      id: task.name,
      type: 'taskNode',
      data: {
        label: formatTaskName(task.name),
        taskState: taskStates[task.name],
        taskName: task.name,
      },
      position: { x: 100, y: index * LAYOUT.nodeSpacing },
    }));

    // Create edges with different styles
    const edges: Edge[] = orderedTasks.slice(0, -1).map((task, index) => {
      const nextTask = orderedTasks[index + 1];
      const sourceTaskState = taskStates[task.name];

      // Determine edge style based on checkpoint status
      let animated = !sourceTaskState?.output;
      let style: EdgeStyle = { stroke: '#888', strokeWidth: 2 };
      const markerEnd = { type: MarkerType.ArrowClosed };

      // If there's a checkpoint, customize the edge
      if (sourceTaskState?.checkpoint) {
        if (sourceTaskState.checkpoint.should_continue === true) {
          style = { stroke: '#4caf50', strokeWidth: 3 };
        } else if (sourceTaskState.checkpoint.should_continue === false) {
          style = {
            stroke: '#f44336',
            strokeWidth: 3,
            strokeDasharray: '5,5',
          };
        } else {
          style = {
            stroke: '#ff9800',
            strokeWidth: 3,
            strokeDasharray: '5,5',
          };
          animated = true;
        }
      }

      return {
        id: `e${task.name}-${nextTask.name}`,
        source: task.name,
        target: nextTask.name,
        animated,
        style,
        markerEnd,
        label: sourceTaskState?.checkpoint ? 'Checkpoint' : undefined,
        labelStyle: { fill: '#fff', fontWeight: 'bold' },
        labelBgStyle: { fill: '#333', fillOpacity: 0.7 },
      };
    });

    // Add workflow input node if data is available
    if (workflow.data) {
      const inputNodeId = 'workflow-input';
      nodes.unshift({
        id: inputNodeId,
        type: 'default',
        data: {
          label: (
            <div style={{ padding: '10px', color: 'var(--bq-text-primary)' }}>
              <div
                style={{
                  fontWeight: 'bold',
                  fontSize: '14px',
                  marginBottom: '5px',
                }}
              >
                Workflow Input
              </div>
              <div style={{ fontSize: '12px' }}>
                {workflow.data.type && <div>Type: {workflow.data.type}</div>}
                {workflow.data.from_address && (
                  <div>From: {workflow.data.from_address}</div>
                )}
                {workflow.data.subject && (
                  <div>Subject: {workflow.data.subject}</div>
                )}
              </div>
            </div>
          ),
        },
        position: { x: 100, y: -150 },
        style: {
          background: 'var(--bq-surface-elevated)',
          border: '2px solid #5caedc',
          borderRadius: '5px',
          color: 'var(--bq-text-primary)',
          width: '250px',
        },
      });

      // Add edge from input to first task
      if (orderedTasks.length > 0) {
        edges.unshift({
          id: `e${inputNodeId}-${orderedTasks[0].name}`,
          source: inputNodeId,
          target: orderedTasks[0].name,
          style: { stroke: '#5caedc', strokeWidth: 2 },
          animated: true,
          markerEnd: { type: MarkerType.ArrowClosed },
        });
      }
    }

    return { nodes, edges };
  }, [workflow.task_states, workflow.data]);

  return (
    <div style={{ width: '100%', height: height, position: 'relative' }}>
      <Tooltip target=".checkpoint-badge" />

      <ReactFlow
        nodes={nodes}
        edges={edges}
        onNodesChange={onNodesChange}
        onEdgesChange={onEdgesChange}
        nodeTypes={nodeTypes}
        fitView
        proOptions={{ hideAttribution: true }}
        defaultEdgeOptions={{
          type: 'smoothstep',
          style: { strokeWidth: 2 },
        }}
        style={{ background: '#1e1e1e' }}
      >
        <Background color="#555" gap={16} />

        <MiniMap
          nodeStrokeWidth={3}
          zoomable
          pannable
          style={{
            backgroundColor: 'var(--bq-surface-muted)',
            height: 120,
            width: 200,
          }}
          nodeColor={(node) => {
            const taskState = node.data?.taskState as TaskState;
            return taskState
              ? getStatusColor(taskState)
              : node.id === 'workflow-input'
                ? '#5caedc'
                : '#999';
          }}
        />

        <div
          style={{
            position: 'absolute',
            top: 10,
            right: 10,
            zIndex: 10,
            display: 'flex',
            gap: '8px',
          }}
        >
          <Button
            label="Fit View"
            icon="pi pi-search"
            onClick={fitView}
            severity="info"
            outlined
            size="small"
            tooltipOptions={{ position: 'top' }}
            tooltip="Fit all nodes in view"
          />
          <Button
            label="Reset Layout"
            icon="pi pi-sort-alt"
            onClick={onLayout}
            severity="secondary"
            outlined
            size="small"
            tooltipOptions={{ position: 'top' }}
            tooltip="Reorganize nodes vertically"
          />
        </div>
      </ReactFlow>
    </div>
  );
};

// Wrapper component with ReactFlowProvider
export const WorkflowGraph = ({
  workflow,
  className = '',
}: WorkflowGraphProps) => {
  const [fullscreen, setFullscreen] = useState(false);

  return (
    <Card
      className={`workflow-graph-card ${className}`}
      pt={{
        root: {
          style: {
            backgroundColor: 'var(--bq-surface-elevated)',
            color: 'var(--bq-text-primary)',
            border: 'none',
            borderRadius: '4px',
            overflow: 'hidden',
            height: '100%',
          },
        },
        content: {
          style: {
            padding: 0,
            height: '70vh',
            minHeight: '600px',
          },
        },
      }}
      title={
        <div className="flex justify-content-between align-items-center">
          <span>Workflow Process Visualization</span>
          <div className="flex align-items-center">
            <span className="mr-2 text-sm">
              Status: <StatusBadge status={workflow.status} />
            </span>
            <Button
              icon="pi pi-window-maximize"
              severity="secondary"
              text
              rounded
              onClick={() => setFullscreen(true)}
              tooltip="View in fullscreen"
              tooltipOptions={{ position: 'top' }}
            />
          </div>
        </div>
      }
    >
      <ReactFlowProvider>
        <FlowGraph workflow={workflow} />
      </ReactFlowProvider>

      <Dialog
        header={`Workflow Process Visualization: ${workflow.title || workflow.workflow_id}`}
        visible={fullscreen}
        style={{ width: '95vw', height: '90vh' }}
        onHide={() => setFullscreen(false)}
        maximizable
        closeOnEscape
      >
        <ReactFlowProvider>
          <FlowGraph workflow={workflow} height="calc(90vh - 120px)" />
        </ReactFlowProvider>
      </Dialog>
    </Card>
  );
};
